import { useState, useMemo } from 'react';

const fmt = (n: number) => `${Math.round(n).toLocaleString('pl-PL')} zł`;

const annuity = (K: number, r: number, n: number): number => {
  const m = r / 1200;
  if (m === 0) return K / n;
  return (K * m) / (1 - Math.pow(1 + m, -n));
};

function SliderField({
  label,
  value,
  display,
  min,
  max,
  step,
  marks,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  display: React.ReactNode;
  min: number;
  max: number;
  step: number;
  marks: [string, string, string];
  onChange: (v: number) => void;
  hint?: string;
}) {
  return (
    <div className="mb-5">
      <div className="flex justify-between items-baseline mb-2">
        <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-wider">{label}</span>
        <span className="text-sm font-extrabold text-foreground-950 tracking-tight">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full cursor-pointer"
      />
      <div className="flex justify-between text-[10px] font-semibold text-foreground-400 mt-1">
        <span>{marks[0]}</span>
        <span>{marks[1]}</span>
        <span>{marks[2]}</span>
      </div>
      {hint && <p className="text-[11px] text-foreground-400 mt-2 font-medium leading-relaxed">{hint}</p>}
    </div>
  );
}

export default function RefinancingCalculator() {
  const [balance, setBalance] = useState(400000);
  const [years, setYears] = useState(22);
  const [rateOld, setRateOld] = useState(7.5);
  const [rateNew, setRateNew] = useState(5.6);
  const [fee, setFee] = useState(3000);

  const n = years * 12;

  const { oldRata, newRata, diff, total, net, brkMonths, isPositive } = useMemo(() => {
    const oldRata = annuity(balance, rateOld, n);
    const newRata = annuity(balance, rateNew, n);
    const diff = oldRata - newRata;
    const total = diff * n;
    const net = total - fee;
    const brkMonths = diff > 0 ? Math.ceil(fee / diff) : null;
    return { oldRata, newRata, diff, total, net, brkMonths, isPositive: diff > 10 };
  }, [balance, rateOld, rateNew, fee, n]);

  const hintText = useMemo(() => {
    if (isPositive) {
      if (net > 20000) {
        return (
          <>
            Realna oszczędność rzędu <b>{fmt(net)}</b> to kwota, o którą warto zawalczyć. Ekspert kredytowy sprawdzi też, czy nie uzyskasz stawki niższej niż założona.
          </>
        );
      }
      return (
        <>
          Oszczędność jest zauważalna. Warto, aby ekspert kredytowy porównał pełne oferty — czasem różnica robi się większa po uwzględnieniu prowizji i ubezpieczeń.
        </>
      );
    }
    return (
      <>
        Twoje oprocentowanie wygląda konkurencyjnie. Mimo to warto raz na rok zweryfikować umowę — ekspert kredytowy bezpłatnie sprawdzi, czy nie ma lepszych warunków. <b>Zadzwoń: 698 084 044.</b>
      </>
    );
  }, [isPositive, net]);

  return (
    <div>
      {/* Hero */}
      <section className="bg-foreground-950 text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary-400 mb-3 opacity-0 animate-slide-up animation-delay-100">
            Kalkulator · Refinansowanie
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mx-auto max-w-2xl text-center leading-tight mb-4 opacity-0 animate-slide-up animation-delay-200">
            Ile zaoszczędzisz, przenosząc kredyt do tańszego banku?
          </h1>
          <p className="text-sm md:text-base text-white/60 mx-auto max-w-xl text-center leading-relaxed opacity-0 animate-slide-up animation-delay-300">
            Jeśli Twój kredyt ma oprocentowanie sprzed obniżek stóp, prawdopodobnie co miesiąc przepłacasz. Porównaj obecną ratę z ratą po refinansowaniu na dzisiejszych warunkach.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 items-start">
            {/* Inputs */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Twój obecny kredyt
              </h3>

              <SliderField
                label="Pozostało do spłaty"
                value={balance}
                display={
                  <>
                    {balance.toLocaleString('pl-PL')}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">zł</span>
                  </>
                }
                min={50000}
                max={1500000}
                step={10000}
                marks={['50 tys.', '750 tys.', '1,5 mln']}
                onChange={setBalance}
              />

              <SliderField
                label="Pozostały okres spłaty"
                value={years}
                display={
                  <>
                    {years}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">
                      {years === 1 ? 'rok' : years < 5 ? 'lata' : 'lat'}
                    </span>
                  </>
                }
                min={1}
                max={35}
                step={1}
                marks={['1', '17', '35']}
                onChange={setYears}
              />

              <SliderField
                label="Obecne oprocentowanie"
                value={rateOld}
                display={
                  <>
                    {rateOld.toFixed(2).replace('.', ',')}
                    <span className="text-foreground-400 font-semibold text-xs">%</span>
                  </>
                }
                min={3}
                max={12}
                step={0.05}
                marks={['3%', '7,5%', '12%']}
                onChange={setRateOld}
              />

              <SliderField
                label="Oprocentowanie po refinansowaniu"
                value={rateNew}
                display={
                  <>
                    {rateNew.toFixed(2).replace('.', ',')}
                    <span className="text-foreground-400 font-semibold text-xs">%</span>
                  </>
                }
                min={3}
                max={12}
                step={0.05}
                marks={['3%', '7,5%', '12%']}
                onChange={setRateNew}
                hint="Domyślnie: najlepsza stawka z rankingu lipiec 2026 (5,60%). Ekspert kredytowy sprawdzi, na jaką stawkę realnie się kwalifikujesz."
              />

              <SliderField
                label="Szacunkowe koszty przeniesienia"
                value={fee}
                display={
                  <>
                    {fee.toLocaleString('pl-PL')}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">zł</span>
                  </>
                }
                min={0}
                max={20000}
                step={500}
                marks={['0', '10 tys.', '20 tys.']}
                onChange={setFee}
              />
            </div>

            {/* Results */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Twoja oszczędność
              </h3>

              <div className={`rounded-xl text-white p-6 text-center ${isPositive ? 'bg-green-700' : 'bg-foreground-950'}`}>
                <div className={`text-[10px] font-bold tracking-[0.15em] uppercase ${isPositive ? 'text-green-300' : 'text-primary-400'}`}>
                  {isPositive ? 'Oszczędność na racie' : 'Wynik porównania'}
                </div>
                <div className="text-2xl md:text-3xl font-extrabold tracking-tight mt-1">
                  {isPositive ? `${fmt(diff)} / mies.` : diff < -10 ? 'Obecna rata jest niższa' : 'Raty są porównywalne'}
                </div>
                <div className={`text-sm mt-1 ${isPositive ? 'text-green-200' : 'text-white/60'}`}>
                  {isPositive ? `czyli ${fmt(diff * 12)} rocznie` : 'przy założonych parametrach refinansowanie nie obniży raty'}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="border border-background-200 rounded-xl p-4 text-center">
                  <div className="text-[10px] font-bold tracking-wider uppercase text-foreground-500">Obecna rata</div>
                  <div className="text-lg font-extrabold tracking-tight text-foreground-900 mt-1">{fmt(oldRata)}</div>
                </div>
                <div className="border border-green-200 bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-[10px] font-bold tracking-wider uppercase text-foreground-500">Nowa rata</div>
                  <div className="text-lg font-extrabold tracking-tight text-green-700 mt-1">{fmt(newRata)}</div>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Oszczędność do końca spłaty</span>
                  <b className="text-foreground-900">{isPositive ? fmt(total) : '—'}</b>
                </div>
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Oszczędność po kosztach przeniesienia</span>
                  <b className="text-foreground-900">{isPositive ? fmt(net) : '—'}</b>
                </div>
                <div className="flex justify-between py-2.5 text-sm">
                  <span className="text-foreground-500">Koszty przeniesienia zwracają się po</span>
                  <b className="text-foreground-900">
                    {isPositive && brkMonths !== null
                      ? brkMonths <= n
                        ? `${brkMonths} mies.`
                        : 'dłużej niż okres kredytu'
                      : '—'}
                  </b>
                </div>
              </div>

              <div className={`rounded-xl p-4 mt-5 text-sm leading-relaxed ${isPositive ? 'bg-primary-100 border border-primary-200 text-primary-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
                {hintText}
              </div>

              <p className="text-[11px] text-foreground-400 leading-relaxed mt-4">
                Obliczenia dla rat równych, przy założeniu stałych stóp przez pozostały okres. Rzeczywista oferta refinansowania zależy od LTV, zdolności kredytowej i polityki banku. Koszty przeniesienia obejmują zwykle wycenę nieruchomości, opłaty sądowe i ewentualną prowizję.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="bg-foreground-950 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-primary-500/10 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3">
                Sprawdźmy, ile realnie odzyskasz z Twojej raty
              </h2>
              <p className="text-sm text-white/60 max-w-xl mx-auto mb-8 leading-relaxed">
                Ekspert kredytowy bezpłatnie przeanalizuje umowę, policzy ofertę refinansowania w kilkunastu bankach i zajmie się formalnościami przeniesienia — Ty tylko podpisujesz.
              </p>
              <a
                href="tel:698084044"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-white font-extrabold text-lg rounded-full hover:bg-primary-600 transition-all hover:-translate-y-0.5 shadow-lg shadow-primary-500/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl" />
                698 084 044
              </a>
              <div className="flex flex-wrap gap-5 justify-center mt-6">
                {[
                  'Bezpłatna analiza umowy',
                  'Pomoc w formalnościach',
                  'Bez zobowiązań',
                ].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-xs text-white/50">
                    <i className="ri-check-line text-green-400" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="pb-10 md:pb-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <p className="text-[11px] text-foreground-400 leading-relaxed text-center max-w-3xl mx-auto">
            Kalkulator ma charakter poglądowy i edukacyjny — nie stanowi oferty w rozumieniu art. 66 Kodeksu cywilnego. Decyzję o refinansowaniu warto poprzedzić analizą pełnych kosztów, w tym warunków wcześniejszej spłaty w obecnym banku.
          </p>
        </div>
      </section>
    </div>
  );
}